import { useMemo } from 'react';
import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { platformApi } from '../api/endpoints/platform';
import { applicationApi } from '../api/endpoints/application';
import { applicationPermissionsQueryOptions } from './useApplicationPermissions';
import type { PlatformAuditNameMaps } from '../lib/platformAuditLabels';
import type { ApplicationPermissionLocal } from '../api/types/ApplicationLocal';

interface PlatformAuditNames {
    maps: PlatformAuditNameMaps;
    /** Admins con nombre, para el filtro de usuario. */
    adminOptions: { value: string; label: string }[];
    hasError: boolean;
    retry: () => void;
}

type PermissionsResult = UseQueryResult<{ items: ApplicationPermissionLocal[] }>;

/** Une los catálogos de todas las aplicaciones en un mapa `${applicationId}#${permissionKey}` → nombre. */
function combinePermissions(results: PermissionsResult[]) {
    const names = new Map<string, string>();
    for (const r of results) {
        for (const p of r.data?.items ?? []) {
            names.set(`${p.applicationId}#${p.permissionKey}`, p.displayName);
        }
    }
    const failed = results.filter((r) => r.isError);
    return {
        names,
        hasError: failed.length > 0,
        retry: () => {
            for (const r of failed) void r.refetch();
        },
    };
}

/** Mapas id → nombre para la auditoría de plataforma; `hasError` si alguno no cargó. */
export function usePlatformAuditNames(): PlatformAuditNames {
    const admins = useQuery({
        queryKey: ['platform', 'users', 'audit-actor-map'],
        queryFn: () => platformApi.listPlatformAdmins(),
    });
    const tenants = useQuery({
        queryKey: ['platform', 'tenants', 'audit-target-map'],
        queryFn: () => platformApi.listTenants(),
    });
    const roles = useQuery({
        queryKey: ['platform', 'roles', 'audit-target-map'],
        queryFn: () => platformApi.listPlatformRoles(),
    });
    const apps = useQuery({
        queryKey: ['applications'],
        queryFn: () => applicationApi.list(),
    });
    const appIds = useMemo(() => (apps.data?.items ?? []).map((a) => a.applicationId), [apps.data]);
    const permissions = useQueries({
        queries: appIds.map((id) => applicationPermissionsQueryOptions(id)),
        combine: combinePermissions,
    });

    const maps = useMemo<PlatformAuditNameMaps>(
        () => ({
            admins: new Map(
                (admins.data?.items ?? []).map((u) => [u.cognitoSub, u.displayName || u.email]),
            ),
            tenants: new Map((tenants.data?.items ?? []).map((t) => [t.tenantId, t.displayName])),
            roles: new Map((roles.data?.items ?? []).map((r) => [r.roleId, r.displayName])),
            applications: new Map(
                (apps.data?.items ?? []).map((a) => [a.applicationId, a.displayName]),
            ),
            permissions: permissions.names,
        }),
        [admins.data, tenants.data, roles.data, apps.data, permissions.names],
    );

    const adminOptions = useMemo(
        () =>
            [...maps.admins]
                .filter(([, name]) => name.trim())
                .map(([value, label]) => ({ value, label })),
        [maps.admins],
    );

    const failed = [admins, tenants, roles, apps].filter((q) => q.isError);
    return {
        maps,
        adminOptions,
        hasError: failed.length > 0 || permissions.hasError,
        retry: () => {
            for (const q of failed) void q.refetch();
            permissions.retry();
        },
    };
}
